/** Student filters for the management {@link DataTable}. */
import * as React from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/forms/Button';
import { SearchInput } from '@/components/forms/SearchInput';
import { Select, type SelectOption } from '@/components/forms/Select';
import { Autocomplete } from '@/components/forms/Autocomplete';
import { MultiSelect } from '@/components/forms/MultiSelect';
import { useQuery } from '@tanstack/react-query';
import { useProgramsForMode } from '@/hooks/useProgramsForMode';
import { list as listParentsRemote } from '@/services/parents.service';
import { usePartnersOptions } from '@/hooks/usePartners';
import { useEstablishmentsAutocomplete } from '@/hooks/useEstablishmentsAutocomplete';
import { useLevelsSelect } from '@/hooks/useLevelsSelect';
import { useDepartments } from '@/hooks/useDepartments';
import { useConnectedUser } from '@/hooks/useConnectedUser';
import { getErrorMessage } from '@/utils/errors-utils';
import {
  getStudentRemoteStatusFilterKeys,
  mapParentsRemoteSearchOptions,
} from '@/components/students/remote/student-table-utils';
import { ManagedByMultiSelect } from '@/components/common/ManagedByMultiSelect';
import {
  type StudentRemoteFilterGroupId as FilterGroupId,
  type StudentRemoteTabView as TabView,
  type StudentRemoteFilters as StudentFilters,
  STUDENT_REGISTRATION_STEP_OPTIONS,
  STUDENT_ACTIVE_TAB_SITUATION_FILTER_KEYS,
  STUDENT_STATUS_FILTER_CHIP_CONFIG,
  type StudentRemoteStatusFilterKey,
  STUDENT_SITUATION_CONFIG,
  GENDER_APPEARANCE,
  STUDENT_GENDER_OPTIONS_WITHOUT_OTHER as GENDER_OPTIONS,
  REMOTE_SUBJECT_OPTIONS,
  REMOTE_SUBJECT_CONFIG,
  BOOLEAN_PARTICIPATION_OPTIONS,
} from '@/components/students/student-configs';

export type { StudentFilters, FilterGroupId };
export {
  DEFAULT_STUDENT_REMOTE_FILTERS as DEFAULT_STUDENT_FILTERS,
  ALL_STUDENT_REMOTE_FILTER_GROUP_IDS as ALL_STUDENT_FILTER_GROUP_IDS,
  ALL_STUDENT_MATCHING_FILTER_GROUP_IDS,
  STUDENT_REMOTE_FILTER_LABELS as STUDENT_FILTER_GROUP_LABELS,
} from '@/components/students/student-configs';

/** Props for {@link StudentFiltersContent}. */
interface Props {
  filters: StudentFilters;
  visibleFilterIds: FilterGroupId[];
  onFilterChange: (patch: Partial<StudentFilters>) => void;
  activeTab: TabView;
}

/** Student filters for the management {@link DataTable}. */
export const StudentFiltersContent: React.FC<Props> = ({
  filters,
  visibleFilterIds,
  onFilterChange,
  activeTab,
}) => {
  const show = (id: FilterGroupId) => visibleFilterIds.includes(id);

  // Locked badge shared style
  const lockedBadgeClass =
    'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg filter-badge-active cursor-default select-none';

  return (
    <div className="filter-groups-container">
      {show('filter-onboarding-status') && (
        <StatusFilter filters={filters} onFilterChange={onFilterChange} activeTab={activeTab} />
      )}

      {show('filter-folder-situation') && activeTab !== 'archived' && (
        <div className="filter-group">
          <span className="filter-group-label">Situation</span>
          <div className="filter-options">
            {activeTab === 'pre_registered'
              ? /* Pre-registered: locked registration-in-progress chip */
                (() => {
                  const cfg = STUDENT_SITUATION_CONFIG.registration_in_progress;
                  const Icon = cfg.icon;
                  return (
                    <Button
                      variant="custom"
                      size="sm"
                      className={lockedBadgeClass}
                      style={{ '--filter-color': cfg.badgeColor } as React.CSSProperties}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      {cfg.label}
                    </Button>
                  );
                })()
              : /* Students tab: selectable situation chips */
                STUDENT_ACTIVE_TAB_SITUATION_FILTER_KEYS.map((key) => {
                  const cfg = STUDENT_SITUATION_CONFIG[key];
                  const isActive = filters.studentSituation === key;
                  const Icon = cfg.icon;
                  return (
                    <Button
                      key={key}
                      variant="custom"
                      size="sm"
                      className={cn(
                        'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-all',
                        isActive ? 'filter-badge-active' : 'filter-badge-inactive',
                      )}
                      style={{ '--filter-color': cfg.badgeColor } as React.CSSProperties}
                      onClick={() =>
                        onFilterChange({ studentSituation: isActive ? undefined : key })
                      }
                    >
                      <Icon className="w-3.5 h-3.5" />
                      {cfg.label}
                    </Button>
                  );
                })}
          </div>
        </div>
      )}

      {show('filter-registration-step') && activeTab === 'pre_registered' && (
        <div className="filter-group">
          <span className="filter-group-label">Étape d'inscription</span>
          <div className="filter-options">
            {STUDENT_REGISTRATION_STEP_OPTIONS.map((opt) => {
              const isActive = (filters.stepKeys ?? []).includes(opt.value);
              const Icon = opt.icon;
              return (
                <Button
                  key={opt.value}
                  variant="custom"
                  size="sm"
                  className={cn(
                    'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-all',
                    isActive ? 'filter-badge-active' : 'filter-badge-inactive',
                  )}
                  style={{ '--filter-color': opt.color } as React.CSSProperties}
                  onClick={() => {
                    const current = filters.stepKeys ?? [];
                    onFilterChange({
                      stepKeys: isActive
                        ? current.filter((k) => k !== opt.value)
                        : [...current, opt.value],
                    });
                  }}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {opt.label}
                </Button>
              );
            })}
          </div>
        </div>
      )}

      {show('filter-linked-parent') && (
        <div className="filter-group">
          <span className="filter-group-label">Parent</span>
          <ParentFilter filters={filters} onFilterChange={onFilterChange} />
        </div>
      )}

      {show('filter-search') && (
        <div className="filter-group">
          <span className="filter-group-label">Recherche</span>
          <div className="relative w-64">
            <SearchInput
              size="sm"
              shortcut={false}
              placeholder="Rechercher un élève..."
              value={filters.search ?? ''}
              onChange={(e) => onFilterChange({ search: e.target.value || undefined })}
              onClear={() => onFilterChange({ search: undefined })}
            />
          </div>
        </div>
      )}

      {show('filter-program') && (
        <ProgramFilter filters={filters} onFilterChange={onFilterChange} />
      )}

      {show('filter-subject') && (
        <div className="filter-group">
          <span className="filter-group-label">Matière</span>
          <div className="filter-options">
            {REMOTE_SUBJECT_OPTIONS.map((opt) => {
              const isActive = filters.subject === opt.value;
              const config = REMOTE_SUBJECT_CONFIG[opt.value];
              const IconComponent = config?.icon;
              return (
                <Button
                  key={opt.value}
                  variant="custom"
                  size="sm"
                  className={cn(
                    'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-all',
                    isActive ? 'filter-badge-active' : 'filter-badge-inactive',
                  )}
                  style={{ '--filter-color': config?.badgeColor } as React.CSSProperties}
                  onClick={() => onFilterChange({ subject: isActive ? undefined : opt.value })}
                >
                  {IconComponent && <IconComponent className="w-3.5 h-3.5" />}
                  {opt.label}
                </Button>
              );
            })}
          </div>
        </div>
      )}

      {show('filter-level') && <LevelFilter filters={filters} onFilterChange={onFilterChange} />}

      {show('filter-partner') && (
        <PartnerFilter filters={filters} onFilterChange={onFilterChange} />
      )}

      {show('filter-department') && (
        <DepartmentFilter filters={filters} onFilterChange={onFilterChange} />
      )}

      {show('filter-establishment') && (
        <EstablishmentFilter filters={filters} onFilterChange={onFilterChange} />
      )}

      {show('filter-managed-by') && (
        <div className="filter-group">
          <span className="filter-group-label">Géré par</span>
          <div className="w-64">
            <ManagedByMultiSelect
              value={filters.managedByIds}
              onChange={(val) => onFilterChange({ managedByIds: val })}
              inputSize="sm"
            />
          </div>
        </div>
      )}

      {show('filter-studies-participation') && (
        <div className="filter-group">
          <span className="filter-group-label">Participation aux études</span>
          <div className="filter-options">
            {BOOLEAN_PARTICIPATION_OPTIONS.map((opt) => {
              const isActive = filters.participationStudies === opt.value;
              return (
                <Button
                  key={String(opt.value)}
                  preset={opt.value ? 'filter-yes' : 'filter-no'}
                  active={isActive}
                  onClick={() =>
                    onFilterChange({
                      participationStudies: isActive ? undefined : opt.value,
                    })
                  }
                >
                  {opt.label}
                </Button>
              );
            })}
          </div>
        </div>
      )}

      {show('filter-gender') && (
        <div className="filter-group">
          <span className="filter-group-label">Genre</span>
          <div className="filter-options">
            {GENDER_OPTIONS.map((opt) => {
              const isActive = filters.gender === opt.value;
              const appearance = GENDER_APPEARANCE[opt.value];
              const IconComponent = appearance.icon;
              return (
                <Button
                  key={opt.value}
                  variant="custom"
                  size="sm"
                  className={cn(
                    'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-all',
                    isActive ? 'filter-badge-active' : 'filter-badge-inactive',
                  )}
                  style={{ '--filter-color': appearance.color } as React.CSSProperties}
                  onClick={() => onFilterChange({ gender: isActive ? undefined : opt.value })}
                >
                  {IconComponent && <IconComponent className="w-3.5 h-3.5" />}
                  {opt.label}
                </Button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

/** Account-status chips scoped to the active remote students tab. */
const StatusFilter: React.FC<{
  filters: StudentFilters;
  onFilterChange: (patch: Partial<StudentFilters>) => void;
  activeTab: TabView;
}> = ({ filters, onFilterChange, activeTab }) => {
  const statusKeys = getStudentRemoteStatusFilterKeys(activeTab);
  const locked = statusKeys.length <= 1;

  function renderStatusChip(statusKey: StudentRemoteStatusFilterKey): React.ReactNode {
    return (
      <StudentStatusChip
        key={statusKey}
        statusKey={statusKey}
        isSelected={filters.userStatus === statusKey}
        locked={locked}
        onFilterChange={onFilterChange}
      />
    );
  }

  if (statusKeys.length === 0) return null;

  return (
    <div className="filter-group">
      <span className="filter-group-label">Statut</span>
      <div className="filter-options">{statusKeys.map(renderStatusChip)}</div>
    </div>
  );
};

/** One exclusive status chip for remote student filters. */
function StudentStatusChip({
  statusKey,
  isSelected,
  locked,
  onFilterChange,
}: Readonly<{
  statusKey: StudentRemoteStatusFilterKey;
  isSelected: boolean;
  locked: boolean;
  onFilterChange: (patch: Partial<StudentFilters>) => void;
}>): React.ReactNode {
  const cfg = STUDENT_STATUS_FILTER_CHIP_CONFIG[statusKey];
  const Icon = cfg.icon;
  const showActive = locked || isSelected;

  function handleClick(): void {
    if (locked) return;
    onFilterChange({ userStatus: isSelected ? undefined : statusKey });
  }

  return (
    <Button
      variant="custom"
      size="sm"
      disabled={locked}
      className={cn(
        'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-all',
        showActive ? 'filter-badge-active' : 'filter-badge-inactive',
        locked && 'opacity-60 cursor-not-allowed',
      )}
      style={
        {
          '--filter-color': cfg.badgeColor ?? 'rgb(var(--status-success))',
        } as React.CSSProperties
      }
      onClick={handleClick}
    >
      <Icon className="w-3.5 h-3.5" />
      {cfg.label}
    </Button>
  );
}

/** Program select for remote student filters. */
const ProgramFilter: React.FC<{
  filters: StudentFilters;
  onFilterChange: (patch: Partial<StudentFilters>) => void;
}> = ({ filters, onFilterChange }) => {
  const { data: programsData } = useProgramsForMode();

  const options = React.useMemo(() => {
    const seen = new Set<string>();
    return (programsData?.data ?? []).flatMap((p) => {
      const id = p.id?.trim();
      if (!id || seen.has(id)) return [];
      seen.add(id);
      return [{ value: id, label: p.name ?? p.label ?? p.code ?? id }];
    });
  }, [programsData]);

  return (
    <div className="filter-group">
      <span className="filter-group-label">Programme</span>
      <div className="w-64">
        <Select
          key={filters.programId ?? '__empty__'}
          placeholder="Tous les programmes"
          size="sm"
          value={filters.programId ?? ''}
          onChange={(v) => onFilterChange({ programId: v || undefined })}
          options={options}
        />
      </div>
    </div>
  );
};

/** Level select for remote student filters. */
const LevelFilter: React.FC<{
  filters: StudentFilters;
  onFilterChange: (patch: Partial<StudentFilters>) => void;
}> = ({ filters, onFilterChange }) => {
  const { options, isLoading, onLoadMore, hasMore } = useLevelsSelect({
    statuses: ['active'],
  });

  return (
    <div className="filter-group">
      <span className="filter-group-label">Niveau de l'élève</span>
      <div className="w-64">
        <Select
          key={filters.levelId ?? '__empty__'}
          placeholder="Tous les niveaux"
          size="sm"
          value={filters.levelId ?? ''}
          onChange={(v) => onFilterChange({ levelId: v || undefined })}
          options={options}
          onLoadMore={onLoadMore}
          hasMore={hasMore}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
};

/** Partner select for remote student filters. */
const PartnerFilter: React.FC<{
  filters: StudentFilters;
  onFilterChange: (patch: Partial<StudentFilters>) => void;
}> = ({ filters, onFilterChange }) => {
  const { data: partnersData, error: partnersError } = usePartnersOptions();

  const options = React.useMemo(
    () => partnersData?.map((p) => ({ value: p.id, label: p.name })) ?? [],
    [partnersData],
  );

  return (
    <div className="filter-group">
      <span className="filter-group-label">Partenaire</span>
      <div className="w-64">
        <Select
          key={filters.partnerId ?? '__empty__'}
          placeholder="Tous les partenaires"
          size="sm"
          value={filters.partnerId ?? ''}
          onChange={(v) => onFilterChange({ partnerId: v || undefined })}
          options={options}
          error={partnersError ? getErrorMessage(partnersError) : undefined}
        />
      </div>
    </div>
  );
};

/** Establishment autocomplete for remote student filters. */
const EstablishmentFilter: React.FC<{
  filters: StudentFilters;
  onFilterChange: (patch: Partial<StudentFilters>) => void;
}> = ({ filters, onFilterChange }) => {
  const { options, onSearch, isLoading } = useEstablishmentsAutocomplete();

  function handleEstablishmentChange(val: string): void {
    onFilterChange({ establishmentId: val || undefined });
  }

  return (
    <div className="filter-group">
      <span className="filter-group-label">Établissement</span>
      <div className="w-64">
        <Autocomplete
          placeholder="Rechercher un établissement..."
          inputSize="sm"
          options={options}
          value={filters.establishmentId ?? ''}
          onChange={handleEstablishmentChange}
          onSearch={onSearch}
          isLoading={isLoading}
          minSearchLength={2}
        />
      </div>
    </div>
  );
};

/** Search remote parents for the parent filter autocomplete. */
function useParentsAutocomplete() {
  const [searchQuery, setSearchQuery] = React.useState('');
  const [page, setPage] = React.useState(1);
  const { user } = useConnectedUser();
  const companyId = user?.companyId;

  const { data, isLoading } = useQuery({
    queryKey: ['parents-remote-search', companyId, searchQuery, page],
    queryFn: () =>
      listParentsRemote({
        companyId: companyId!,
        search: searchQuery,
        page,
        pageSize: 10,
      }),
    enabled: searchQuery.length >= 2 && !!companyId,
  });

  const handleSearch = React.useCallback((query: string) => {
    setSearchQuery(query);
    setPage(1);
  }, []);

  const options: SelectOption[] = mapParentsRemoteSearchOptions(data?.data);

  return {
    options,
    isLoading,
    onSearch: handleSearch,
  };
}

/** Parent autocomplete for remote student filters. */
const ParentFilter: React.FC<{
  filters: StudentFilters;
  onFilterChange: (patch: Partial<StudentFilters>) => void;
}> = ({ filters, onFilterChange }) => {
  const { options, onSearch, isLoading } = useParentsAutocomplete();

  return (
    <div className="w-64">
      <Autocomplete
        placeholder="Rechercher un parent..."
        inputSize="sm"
        options={options}
        value={filters.parentId ?? ''}
        onChange={(val) => onFilterChange({ parentId: val || undefined })}
        onSearch={onSearch}
        isLoading={isLoading}
        minSearchLength={2}
      />
    </div>
  );
};

/** French department select for remote student filters. */
const DepartmentFilter: React.FC<{
  filters: StudentFilters;
  onFilterChange: (patch: Partial<StudentFilters>) => void;
}> = ({ filters, onFilterChange }) => {
  const { data: departments = [], error: departmentsError } = useDepartments('FR');

  const options = React.useMemo(
    () => departments.map((dept) => ({ value: dept.code, label: dept.name })),
    [departments],
  );

  return (
    <div className="filter-group">
      <span className="filter-group-label">Département</span>
      <div className="w-64">
        <MultiSelect
          placeholder="Sélectionner des départements..."
          options={options}
          value={filters.departmentCodes ?? []}
          onChange={(values) =>
            onFilterChange({ departmentCodes: values.length > 0 ? values : undefined })
          }
          inputSize="sm"
          error={departmentsError ? getErrorMessage(departmentsError) : undefined}
        />
      </div>
    </div>
  );
};
